// pages/admin/TILManagementPage.tsx - SIMPLIFIED VERSION
// REPLACE THE ENTIRE CONTENTS OF THIS FILE WITH THIS CODE

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Schedule as ClockIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from '../utils/axiosConfig';
import ProcessTILButton from '../components/ProcessTILButton';

interface PersonnelTILSummary {
  _id: string;
  name: string;
  email: string;
  employmentType: string;
  tilBalance: {
    hours: number;
    days: number;
    totalEntries: number;
  };
  annualLeaveBalance: number;
  warnings: {
    highTIL: boolean;
    noAnnualLeave: boolean;
  };
}

interface TILEntry {
  hours: number;
  earnedDate: string;
  expiryDate: string;
  description: string;
  isExpired: boolean;
  daysUntilExpiry: number;
  status: 'expired' | 'expiring-soon' | 'active';
}

interface TILHistory {
  personnel: {
    _id: string;
    name: string;
    email: string;
  };
  tilEntries: TILEntry[];
  summary: {
    totalEarned: number;
    totalUsed: number;
    currentBalance: number;
    entriesCount: number;
    expiringCount: number;
  };
}

const TILManagementPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [tilSummary, setTilSummary] = useState<{
    summary: PersonnelTILSummary[];
    totals: {
      totalStaff: number;
      totalTILHours: number;
      staffWithHighTIL: number;
      staffWithNoAL: number;
    };
  } | null>(null);
  
  const [selectedStaffId, setSelectedStaffId] = useState<string>('');
  const [staffTILHistory, setStaffTILHistory] = useState<TILHistory | null>(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<{
    index: number;
    hours: number;
    earnedDate: string;
    expiryDate: string;
    description: string;
  } | null>(null);
  
  // Add manual TIL dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addData, setAddData] = useState({
    personnelId: '',
    hours: 0,
    description: ''
  });

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    index: number | null;
    entry: TILEntry | null;
  }>({ open: false, index: null, entry: null });

  useEffect(() => {
    fetchTILSummary();
  }, []);

  const fetchTILSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/api/personnel/staff/til-summary');
      setTilSummary(response.data);
    } catch (err: any) {
      console.error('Error fetching TIL summary:', err);
      setError(err.response?.data?.message || 'Failed to load TIL data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffTILDetails = async (staffId: string) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/personnel/${staffId}/leave/til-history`);
      setStaffTILHistory(response.data);
    } catch (err: any) {
      console.error('Error fetching staff TIL details:', err);
      setError(err.response?.data?.message || 'Failed to load staff TIL details');
    } finally {
      setLoading(false);
    }
  };

  const handleStaffSelect = (staffId: string) => {
    setSelectedStaffId(staffId);
    if (staffId) {
      fetchStaffTILDetails(staffId);
    } else {
      setStaffTILHistory(null);
    }
  };

  const handleEditEntry = (index: number, entry: TILEntry) => {
    setEditingEntry({
      index,
      hours: entry.hours,
      earnedDate: entry.earnedDate,
      expiryDate: entry.expiryDate,
      description: entry.description
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEntry || !selectedStaffId) return;
    
    try {
      setError(null);
      await axiosInstance.patch(
        `/api/personnel/${selectedStaffId}/leave/til-entry/${editingEntry.index}`,
        {
          hours: editingEntry.hours,
          earnedDate: editingEntry.earnedDate,
          expiryDate: editingEntry.expiryDate,
          description: editingEntry.description
        }
      );
      
      setSuccess('TIL entry updated successfully');
      setEditDialogOpen(false);
      setEditingEntry(null);
      
      await fetchTILSummary();
      await fetchStaffTILDetails(selectedStaffId);
    } catch (err: any) {
      console.error('Error updating TIL entry:', err);
      setError(err.response?.data?.message || 'Failed to update TIL entry');
    }
  };

  const handleDeleteEntry = async () => {
    if (deleteConfirm.index === null || !selectedStaffId) return;
    
    try {
      setError(null);
      await axiosInstance.delete(
        `/api/personnel/${selectedStaffId}/leave/til-entry/${deleteConfirm.index}`
      );
      
      setSuccess('TIL entry deleted successfully');
      setDeleteConfirm({ open: false, index: null, entry: null });
      
      await fetchTILSummary();
      await fetchStaffTILDetails(selectedStaffId);
    } catch (err: any) {
      console.error('Error deleting TIL entry:', err);
      setError(err.response?.data?.message || 'Failed to delete TIL entry');
    }
  };

  const handleAddManualTIL = async () => {
    try {
      if (!addData.personnelId || addData.hours === 0) {
        setError('Please select staff member and enter valid hours');
        return;
      }
      
      setError(null);
      await axiosInstance.post(`/api/personnel/${addData.personnelId}/leave/add-til`, {
        hours: addData.hours,
        description: addData.description || 'Manual TIL adjustment',
        adminOverride: true
      });
      
      setSuccess('TIL added successfully');
      setAddDialogOpen(false);
      setAddData({ personnelId: '', hours: 0, description: '' });
      
      await fetchTILSummary();
      if (selectedStaffId === addData.personnelId) {
        await fetchStaffTILDetails(selectedStaffId);
      }
    } catch (err: any) {
      console.error('Error adding TIL:', err);
      setError(err.response?.data?.message || 'Failed to add TIL');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'expired': return 'error';
      case 'expiring-soon': return 'warning';
      case 'active': return 'success';
      default: return 'default';
    }
  };

  const getTILWarningLevel = (hours: number) => {
    if (hours >= 24) return { level: 'critical', color: 'error.main' };
    if (hours >= 16) return { level: 'high', color: 'warning.main' };
    if (hours >= 8) return { level: 'medium', color: 'info.main' };
    return { level: 'normal', color: 'success.main' };
  };

  if (loading && !tilSummary) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading TIL management...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Time in Lieu Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage staff time in lieu balances and compliance with company guidelines.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <ProcessTILButton />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {tilSummary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {tilSummary.totals.totalStaff}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Staff Members
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ClockIcon sx={{ fontSize: 40, color: 'info.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {tilSummary.totals.totalTILHours}h
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total TIL Hours
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <WarningIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {tilSummary.totals.staffWithHighTIL}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      High TIL (over 16h)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {Math.round(tilSummary.totals.totalTILHours / 8)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      TIL Days Total
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Staff Overview</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={() => setAddDialogOpen(true)}
                >
                  Add TIL
                </Button>
              </Box>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Staff Member</TableCell>
                      <TableCell align="right">TIL Balance</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tilSummary?.summary.map((person) => {
                      const tilWarning = getTILWarningLevel(person.tilBalance.hours);
                      const isSelected = selectedStaffId === person._id;
                      
                      return (
                        <TableRow 
                          key={person._id} 
                          hover
                          selected={isSelected}
                          onClick={() => handleStaffSelect(person._id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {person.name}
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              color={tilWarning.color}
                            >
                              {person.tilBalance.hours}h
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="center">
                            {person.warnings.highTIL ? (
                              <Chip label="High" size="small" color="warning" />
                            ) : (
                              <Chip label="OK" size="small" color="success" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          {!selectedStaffId ? (
            <Card>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <PersonIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Select a staff member to view their TIL details
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : staffTILHistory ? (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {staffTILHistory.personnel.name} - TIL Entries
                </Typography>
                
                <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Current Balance</Typography>
                      <Typography variant="h5" color="primary.main" fontWeight="bold">
                        {staffTILHistory.summary.currentBalance}h
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Total Earned</Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {staffTILHistory.summary.totalEarned}h
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Total Used</Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {staffTILHistory.summary.totalUsed}h
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {staffTILHistory.tilEntries.length === 0 ? (
                  <Alert severity="info">No TIL entries found for this staff member.</Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Hours</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {staffTILHistory.tilEntries.map((entry, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2">
                                {format(new Date(entry.earnedDate), 'dd/MM/yyyy')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {entry.description}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {entry.hours}h
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={entry.status.replace('-', ' ')}
                                size="small"
                                color={getStatusColor(entry.status)}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Edit entry">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleEditEntry(index, entry)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete entry">
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => setDeleteConfirm({ open: true, index, entry })}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Edit Entry Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit TIL Entry</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Hours"
              type="number"
              value={editingEntry?.hours || 0}
              onChange={(e) => setEditingEntry(prev => prev ? { ...prev, hours: Number(e.target.value) } : null)}
              fullWidth
            />
            <TextField
              label="Earned Date"
              type="date"
              value={editingEntry?.earnedDate ? format(new Date(editingEntry.earnedDate), 'yyyy-MM-dd') : ''}
              onChange={(e) => setEditingEntry(prev => prev ? { ...prev, earnedDate: e.target.value } : null)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Expiry Date"
              type="date"
              value={editingEntry?.expiryDate ? format(new Date(editingEntry.expiryDate), 'yyyy-MM-dd') : ''}
              onChange={(e) => setEditingEntry(prev => prev ? { ...prev, expiryDate: e.target.value } : null)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Description"
              value={editingEntry?.description || ''}
              onChange={(e) => setEditingEntry(prev => prev ? { ...prev, description: e.target.value } : null)}
              multiline
              rows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, index: null, entry: null })}>
        <DialogTitle>Delete TIL Entry?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this TIL entry?
          </Typography>
          {deleteConfirm.entry && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{deleteConfirm.entry.hours}h</strong> - {deleteConfirm.entry.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Earned: {format(new Date(deleteConfirm.entry.earnedDate), 'dd/MM/yyyy')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, index: null, entry: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteEntry} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Manual TIL Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Manual TIL</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Staff Member</InputLabel>
              <Select
                value={addData.personnelId}
                onChange={(e) => setAddData(prev => ({ ...prev, personnelId: e.target.value }))}
                label="Staff Member"
              >
                {tilSummary?.summary.map((person) => (
                  <MenuItem key={person._id} value={person._id}>
                    {person.name} (Current: {person.tilBalance.hours}h)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Hours"
              type="number"
              value={addData.hours}
              onChange={(e) => setAddData(prev => ({ ...prev, hours: Number(e.target.value) }))}
              helperText="Use negative numbers to remove hours"
              fullWidth
            />

            <TextField
              label="Description"
              value={addData.description}
              onChange={(e) => setAddData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Reason for adjustment"
              multiline
              rows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddManualTIL}
            variant="contained"
            disabled={!addData.personnelId || addData.hours === 0}
          >
            Add TIL
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TILManagementPage; 